import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between h-full" }
const _hoisted_2 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_3 = { class: "max-w-[1040px]" }
const _hoisted_4 = { class: "divide-y divide-gray-100" }
const _hoisted_5 = { class: "max-w-xs" }
const _hoisted_6 = {
  key: 0,
  class: "mt-2 text-sm text-red-600"
}
const _hoisted_7 = { class: "flex justify-end" }
const _hoisted_8 = { class: "flex items-center" }
const _hoisted_9 = {
  key: 0,
  class: "truncate text-gray-900 font-medium"
}
const _hoisted_10 = {
  key: 1,
  class: "text-gray-300"
}
const _hoisted_11 = { class: "flex justify-end" }
const _hoisted_12 = { class: "mb-0" }
const _hoisted_13 = ["innerHTML"]

import { Bars3Icon, XCircleIcon } from '@heroicons/vue/24/outline'
import DOMPurify from 'dompurify'
import Sortable from 'sortablejs'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import InputField from '@/components/neue/InputField.vue'
import SettingsSection from '@/components/neue/SettingsSection.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'

const DEFAULT_FONT_SIZE = 18

const MIN_FONT_SIZE = 16
const MAX_FONT_SIZE = 32


export default {
  __name: 'PackingSlipSettings',
  setup(__props) {

const router = useRouter()
const store = useStore()
const rows = ref(null)

// Mirrors ReportSetting::DEFAULT_FONT_SIZE, MIN_FONT_SIZE and MAX_FONT_SIZE on the server.
const QR_TYPES = {
  none: { name: 'No QR code' },
  store: {
    name: 'Store link',
    description: 'Opens the store the order came from, so the customer can order again.'
  },
  order_view: {
    name: 'Order link in your dashboard',
    description: 'Opens the order in this dashboard. For your team only, it needs a dashboard login.'
  },
  custom: { name: 'Custom URL' }
}
const QR_PRESETS = ['store', 'order_view']
const qrTypeOptions = Object.entries(QR_TYPES).map(([value, { name }]) => ({ name, value }))
const qrTypeFor = qrCodeUrl => {
  if (!qrCodeUrl) return 'none'
  return QR_PRESETS.includes(qrCodeUrl) ? qrCodeUrl : 'custom'
}
const qrCodeUrlFor = ({ qrType, qrCustomUrl }) => {
  if (qrType === 'none') return ''
  return qrType === 'custom' ? qrCustomUrl : qrType
}
const loading = ref(true)
const hasUnsavedChanges = ref(false)
const local = reactive({
  qrType: 'none',
  qrCustomUrl: '',
  fontSize: DEFAULT_FONT_SIZE
})
const fontSize = computed(() => Number(local.fontSize))
const fontSizeValid = computed(
  () => Number.isInteger(fontSize.value) && fontSize.value >= MIN_FONT_SIZE && fontSize.value <= MAX_FONT_SIZE
)
const elementModal = reactive({ open: false })

const setting = computed(() => mapType('report_setting')?.[0])
const variables = computed(() =>
  mapRelationships(setting.value?.relationships.report_setting_variables.data)?.sort((a, b) => a.id - b.id)
)
const elements = computed(() => mapRelationships(setting.value?.relationships.report_setting_content_elements.data))
const logo = computed(() => mapRelationship(setting.value?.relationships.logo.data))
const loggedInUser = computed(() => mapRelationship({ type: 'user', id: store.getters.loggedInUserId }))

const discard = () => {
  loading.value = false
  local.header = setting.value.attributes.header_text
  const qrCodeUrl = setting.value.attributes.qr_code_url
  local.qrType = qrTypeFor(qrCodeUrl)
  local.qrCustomUrl = local.qrType === 'custom' ? qrCodeUrl : ''
  local.sort = setting.value.attributes.default_sorting
  local.fontSize = setting.value.attributes.font_size ?? DEFAULT_FONT_SIZE
  hasUnsavedChanges.value = false
}

watch(rows, () => {
  if (rows.value)
    new Sortable(rows.value, {
      handle: '.cursor-move',
      ghostClass: 'bg-gray-200',
      dragClass: 'bg-white',
      onSort(e) {
        const list = elements.value
        const moved = list.splice(e.oldIndex, 1)[0]
        list.splice(e.newIndex, 0, moved)

        api.call('PUT', `users/${loggedInUser.value.id}/report_settings/${setting.value.id}`, {
          report_setting: {
            report_setting_content_elements_attributes: list.map((x, i) => ({
              id: x.id,
              order: i
            }))
          }
        })
      }
    })
})

const addElement = () => {
  api
    .call(
      'PUT',
      `users/thisfieldisunused/report_settings/${setting.value.id}?included=report_setting_content_elements`,
      {
        report_setting: {
          report_setting_content_elements_attributes: [
            {
              full_width: false
            }
          ]
        }
      }
    )
    .then(async () => {
      editElement(elements.value.reduce((last, x) => (x.attributes.id > last.attributes.id ? x : last)))
      elementModal.new = true
    })
}

const deleteElement = el => {
  api.call('PUT', `users/thisfieldisunused/report_settings/${setting.value.id}?included=none`, {
    report_setting: {
      report_setting_content_elements_attributes: {
        id: el.id,
        _destroy: true
      }
    }
  })
}

const editElement = async el => {
  elementModal.el = el
  elementModal.header = el.attributes.header_text
  elementModal.content = DOMPurify.sanitize(el.attributes.content)
  elementModal.fullWidth = el.attributes.full_width
  elementModal.new = false
  elementModal.open = true
  await nextTick()
  elementModal.quill = new Quill('#quill', {
    theme: 'snow'
  })
}

const changeImage = image => {
  api.call('PUT', `users/thisfieldisunused/report_settings/${setting.value.id}?included=logo`, {
    report_setting: {
      logo_attributes: image
    }
  })
}

const closeElementModal = () => {
  if (elementModal.new) deleteElement(elementModal.el)
  elementModal.open = false
}

const saveElementModal = () => {
  api.call(
    'PUT',
    `users/thisfieldisunused/report_settings/${setting.value.id}?included=report_setting_content_elements`,
    {
      report_setting: {
        report_setting_content_elements_attributes: {
          id: elementModal.el.id,
          header_text: elementModal.header,
          content: elementModal.quill.getSemanticHTML(),
          full_width: elementModal.fullWidth
        }
      }
    }
  )
  elementModal.open = false
}

const save = () => {
  if (!fontSizeValid.value) return
  const qrCodeUrl = qrCodeUrlFor(local)
  api.call('PUT', `users/thisfieldisunused/report_settings/${setting.value.id}?included=none`, {
    report_setting: {
      header_text: local.header,
      qr_code_url: qrCodeUrl,
      default_sorting: local.sort,
      font_size: fontSize.value
    }
  })
  hasUnsavedChanges.value = false
}

const setVariable = (id, enabled) => {
  api.call('PUT', `users/thisfieldisunused/report_settings/${setting.value.id}?included=report_setting_variables`, {
    report_setting: {
      report_setting_variables_attributes: [{ id, enabled }]
    }
  })
}

api
  .call(
    'GET',
    'users/thisfieldisunused/report_settings?included=report_setting_variables,report_setting_content_elements,logo'
  )
  .then(discard)

return (_ctx, _cache) => {
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_SaveBar = _resolveComponent("SaveBar")
  const _component_ImageUploaderNeue = _resolveComponent("ImageUploaderNeue")
  const _component_SelectInput = _resolveComponent("SelectInput")
  const _component_NeueToggle = _resolveComponent("NeueToggle")
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_TablistHeader = _resolveComponent("TablistHeader")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_FormElement = _resolveComponent("FormElement")
  const _component_FormSection = _resolveComponent("FormSection")
  const _component_TheModal = _resolveComponent("TheModal")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_router_link = _resolveComponent("router-link")
  const _component_PageFooter = _resolveComponent("PageFooter")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, {
        text: "Packing slip settings",
        "save-status": true
      }),
      _createVNode(_component_SaveBar, {
        "has-unsaved-changes": hasUnsavedChanges.value,
        onOnDiscard: discard,
        onOnSave: save
      }, null, 8, ["has-unsaved-changes"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_NeueLoading, { loading: loading.value }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(SettingsSection, {
                  title: "Header",
                  description: "The text and logo printed across the top of every packing slip."
                }, {
                  default: _withCtx(() => [
                    _createVNode(InputField, {
                      modelValue: local.header,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((local.header) = $event)),
                      name: "Header text",
                      onInput: _cache[1] || (_cache[1] = $event => (hasUnsavedChanges.value = true))
                    }, null, 8, ["modelValue"]),
                    _createElementVNode("div", null, [
                      _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-sm font-medium text-gray-700" }, "Logo image", -1)),
                      _cache[14] || (_cache[14] = _createElementVNode("div", { class: "text-sm text-gray-500 mb-1.5" }, "250×100 px recommended", -1)),
                      _createVNode(_component_ImageUploaderNeue, {
                        files: logo.value ? [logo.value] : [],
                        multiple: false,
                        onChange: changeImage
                      }, null, 8, ["files"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(SettingsSection, {
                  title: "QR code",
                  description: "An optional QR code on each slip, linking to your store, to the order in your dashboard, or to a page you choose."
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_SelectInput, {
                      modelValue: local.qrType,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((local.qrType) = $event)),
                      name: "QR code options",
                      options: _unref(qrTypeOptions),
                      description: QR_TYPES[local.qrType]?.description,
                      onInput: _cache[3] || (_cache[3] = $event => (hasUnsavedChanges.value = true))
                    }, null, 8, ["modelValue", "options", "description"]),
                    (local.qrType === 'custom')
                      ? (_openBlock(), _createBlock(InputField, {
                          key: 0,
                          modelValue: local.qrCustomUrl,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => ((local.qrCustomUrl) = $event)),
                          name: "Custom URL",
                          onInput: _cache[5] || (_cache[5] = $event => (hasUnsavedChanges.value = true))
                        }, null, 8, ["modelValue"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _createVNode(SettingsSection, {
                  title: "Order details",
                  description: "What each slip shows about the customer and their order."
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(variables.value, (variable) => {
                        return (_openBlock(), _createBlock(_component_NeueToggle, {
                          key: variable.id,
                          class: "py-2.5 first:pt-0 last:pb-0",
                          name: variable.attributes.name,
                          "model-value": variable.attributes.enabled,
                          onChange: e => setVariable(variable.id, e)
                        }, null, 8, ["name", "model-value", "onChange"]))
                      }), 128))
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(SettingsSection, {
                  title: "Product sorting",
                  description: "The order products are listed in on each slip."
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_SelectInput, {
                      modelValue: local.sort,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((local.sort) = $event)),
                      name: "Sort products",
                      options: [
                    { name: 'Alphabetical by product', value: 'product_alphabetical' },
                    { name: 'Category order then alphabetical', value: 'store_category_product_alphabetical' },
                    { name: 'By product order in store', value: 'store_product' }
                  ],
                      onInput: _cache[7] || (_cache[7] = $event => (hasUnsavedChanges.value = true))
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(SettingsSection, {
                  title: "Line items",
                  description: "How each product line is printed on the slip."
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(InputField, {
                        modelValue: local.fontSize,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = $event => ((local.fontSize) = $event)),
                        name: "Line item font size",
                        restrict: "integer",
                        suffix: "px",
                        description: "Sets the size of the quantity and product name on each line.",
                        error: !fontSizeValid.value,
                        onInput: _cache[9] || (_cache[9] = $event => (hasUnsavedChanges.value = true))
                      }, null, 8, ["modelValue", "error"]),
                      (!fontSizeValid.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_6, " Enter a size between " + _toDisplayString(MIN_FONT_SIZE) + "px and " + _toDisplayString(MAX_FONT_SIZE) + "px. "))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(SettingsSection, {
                  title: "Content elements",
                  description: "Extra blocks of text printed on each slip, like instructions or a thank-you note. Drag to reorder.",
                  plain: ""
                }, {
                  default: _withCtx(() => [
                    (elements.value.length)
                      ? (_openBlock(), _createBlock(_component_NeueTablist, { key: 0 }, {
                          head: _withCtx(() => [
                            _createVNode(_component_TablistHeader, null, {
                              default: _withCtx(() => [
                                _createVNode(TablistGrid, {
                                  columns: ['Header', 'Layout', ''],
                                  header: "",
                                  class: "items-center"
                                }, {
                                  default: _withCtx(() => [
                                    _cache[16] || (_cache[16] = _createElementVNode("div", null, "Header", -1)),
                                    _cache[17] || (_cache[17] = _createElementVNode("div", null, "Layout", -1)),
                                    _createElementVNode("div", _hoisted_7, [
                                      _createVNode(_component_NeueButton, {
                                        type: "secondary",
                                        onClick: addElement
                                      }, {
                                        default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                          _createTextVNode("+ Element", -1)
                                        ]))]),
                                        _: 1
                                      })
                                    ])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          default: _withCtx(() => [
                            _createElementVNode("div", {
                              ref_key: "rows",
                              ref: rows,
                              class: "divide-y divide-gray-200"
                            }, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(elements.value, (el) => {
                                return (_openBlock(), _createBlock(_component_TablistRow, {
                                  key: el.id,
                                  class: "cursor-pointer",
                                  onClick: $event => (editElement(el))
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(TablistGrid, {
                                      columns: ['Header', 'Layout', ''],
                                      class: "items-center"
                                    }, {
                                      default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_8, [
                                          _createElementVNode("div", {
                                            class: "cursor-move -ml-4 px-3 -mt-2 -mb-2 py-2",
                                            onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"]))
                                          }, [
                                            _createVNode(_unref(Bars3Icon), { class: "h-5 w-5" })
                                          ]),
                                          (el.attributes.header_text)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(el.attributes.header_text), 1))
                                            : (_openBlock(), _createElementBlock("span", _hoisted_10, "(no header)"))
                                        ]),
                                        _createElementVNode("div", null, _toDisplayString(el.attributes.full_width ? 'Full-width' : 'In-line'), 1),
                                        _createElementVNode("div", _hoisted_11, [
                                          _createVNode(_unref(XCircleIcon), {
                                            class: "h-6 w-6 hover:text-black shrink-0",
                                            onClick: _withModifiers($event => (deleteElement(el)), ["stop"])
                                          }, null, 8, ["onClick"])
                                        ])
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]),
                                  _: 2
                                }, 1032, ["onClick"]))
                              }), 128))
                            ], 512)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (!elements.value.length)
                      ? (_openBlock(), _createBlock(_component_EmptyState, {
                          key: 1,
                          plural: "content elements",
                          class: "rounded-xl border border-gray-200 bg-white px-6 py-10 shadow-sm"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_NeueButton, {
                              type: "secondary",
                              onClick: _withModifiers(addElement, ["prevent"])
                            }, {
                              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                _createTextVNode("+ Element", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_TheModal, {
                "is-open": elementModal.open,
                "modal-title": "Edit content element",
                onCloseModal: closeElementModal
              }, {
                main: _withCtx(() => [
                  _createVNode(_component_FormSection, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_FormElement, null, {
                        default: _withCtx(() => [
                          _createVNode(InputField, {
                            modelValue: elementModal.header,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = $event => ((elementModal.header) = $event)),
                            name: "Header text"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_FormElement, null, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_12, [
                            _createElementVNode("div", {
                              id: "quill",
                              innerHTML: elementModal.content
                            }, null, 8, _hoisted_13)
                          ])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_FormElement, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_NeueToggle, {
                            modelValue: elementModal.fullWidth,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = $event => ((elementModal.fullWidth) = $event)),
                            name: "Full-width"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                footer: _withCtx(() => [
                  _createVNode(_component_NeueButton, {
                    type: "secondary",
                    onClick: closeElementModal
                  }, {
                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                      _createTextVNode("Cancel", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_NeueButton, {
                    type: "primary",
                    onClick: saveElementModal
                  }, {
                    default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                      _createTextVNode("Save", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["is-open"])
            ]),
            _: 1
          }, 8, ["loading"])
        ]),
        _createVNode(_component_PageFooter, null, {
          right: _withCtx(() => [
            (hasUnsavedChanges.value)
              ? (_openBlock(), _createBlock(_component_NeueButton, {
                  key: 0,
                  type: "primary",
                  onClick: save
                }, {
                  default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                    _createTextVNode("Save", -1)
                  ]))]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(_component_router_link, {
                  key: 1,
                  to: "/store/products"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_NeueButton, { type: "primary" }, {
                      default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                        _createTextVNode("Done", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }))
}
}

}